import React from 'react';
import { Modal, Text, Loader, Stack, Divider, Group, ActionIcon, rem } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { Search } from 'tabler-icons-react';
import { spotlight } from '@mantine/spotlight';
import { SearchResponse, getOrderedGroups, groupResultsByType } from './utils/searchHelpers';
import SearchResultItem from './SearchResultItem';

interface SearchModalProps {
  opened: boolean;
  onClose: () => void;
  searchQuery: string;
  fullSearchResults: SearchResponse | null;
  isLoading: boolean;
  onBackToSearch: () => void;
}

const SearchModal: React.FC<SearchModalProps> = ({
  opened,
  onClose,
  searchQuery,
  fullSearchResults,
  isLoading,
  onBackToSearch,
}) => {
  const isMobile = useMediaQuery('(max-width: 576px)') ?? false;

  const handleBackToSearch = () => {
    onBackToSearch();
    setTimeout(() => {
      spotlight.open();
    }, 100); // Small delay to ensure modal closes first
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center items-center py-8">
          <Loader size="lg" color="orange" />
        </div>
      );
    }

    if (!fullSearchResults) {
      return null;
    }

    if (fullSearchResults.total_count === 0) {
      return (
        <div className={isMobile ? 'p-2' : 'p-4'}>
          <Text c="gray" size="sm" ta="center" py="xl">
            No results found for "{fullSearchResults.query}"
          </Text>
        </div>
      );
    }

    const groupedResults = groupResultsByType(fullSearchResults.results);
    const orderedGroups = getOrderedGroups(groupedResults);

    return (
      <div className={isMobile ? 'p-2' : 'p-4'}>
        <Text size="sm" c="gray" className="mb-3">
          Found {fullSearchResults.total_count} results
        </Text>

        {orderedGroups.map((group) => (
          <div key={group.name} className="mb-6">
            <Text size="md" fw={600} c="white" className="mb-2">
              {group.name} ({group.results.length})
            </Text>
            <Stack gap="xs">
              {group.results.map((result) => (
                <SearchResultItem
                  key={result.id}
                  result={result}
                  isMobile={isMobile}
                  onClick={() => (window.location.href = result.url)}
                />
              ))}
            </Stack>
            <Divider my="md" color="#374151" />
          </div>
        ))}
      </div>
    );
  };

  const modalTitle = (
    <Group justify="space-between" style={{ width: '100%' }}>
      <ActionIcon
        variant="filled"
        color="#EA580C"
        size="xl"
        onClick={handleBackToSearch}
        title="Back to Search"
        style={{ marginRight: rem(8) }}
      >
        <Search style={{ width: rem(24), height: rem(24) }} />
      </ActionIcon>
      <Text size="lg" fw={600}>
        Search Results for "{fullSearchResults?.query || searchQuery}"
      </Text>
    </Group>
  );

  const shouldShowScrollableHeight = fullSearchResults && fullSearchResults.results.length > 10;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="xl"
      title={modalTitle}
      styles={{
        content: {
          backgroundColor: '#171717',
        },
        header: {
          backgroundColor: '#171717',
          borderBottom: '1px solid #374151',
        },
        title: {
          color: '#ffffff',
        },
        close: {
          color: '#ffffff',
          '&:hover': {
            backgroundColor: '#374151',
          },
        },
        body: {
          backgroundColor: '#171717',
          padding: 0,
        },
      }}
    >
      <div
        style={{
          height: shouldShowScrollableHeight ? '70vh' : 'auto',
          maxHeight: '80vh',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {renderContent()}
      </div>
    </Modal>
  );
};

export default SearchModal;
